import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { STORAGE_KEYS } from "service/storage";

export const accessTokenRequestInterceptor =
  <T extends AxiosRequestConfig>(
    getAccessToken: () => Promise<string | undefined>,
  ) =>
  async (config: T) => {
    config.headers = config.headers ?? {};

    if (localStorage.getItem(STORAGE_KEYS.CDC_USER)) {
      const { CdcTokenService } = await import("service/cdc-token.service");
      if (CdcTokenService.isTokenExpiringSoon()) {
        await CdcTokenService.refreshToken("request-interceptor");
      }
    }

    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

// SiteMinder bypass for the local CRA dev proxy (see `proxy` in webapp
// package.json, which forwards localhost traffic to the QA gateway).
// The token is read from REACT_APP_SITEMINDER_BYPASS_TOKEN — set it in a
// local `.env.local`. CI/QA/prod builds leave it undefined so the value
// never ships in the deployed bundle.
const SITEMINDER_BYPASS_PARAM = "_sm_byp";
const getSiteMinderBypassToken = (): string | undefined =>
  process.env.REACT_APP_SITEMINDER_BYPASS_TOKEN;

/**
 * Appends the SiteMinder bypass query parameter to `url` when running on
 * `localhost` and `REACT_APP_SITEMINDER_BYPASS_TOKEN` is set; returns `url`
 * unchanged otherwise. Used for fire-and-forget requests (e.g. sendBeacon)
 * that can't go through the axios interceptor.
 */
export const appendSiteMinderBypass = (url: string): string => {
  if (window.location.hostname !== "localhost") return url;
  const token = getSiteMinderBypassToken();
  if (!token) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${SITEMINDER_BYPASS_PARAM}=${token}`;
};

export const onSiteProxyRequestInterceptor = (
  config: InternalAxiosRequestConfig,
) => {
  const token = getSiteMinderBypassToken();
  if (window.location.hostname === "localhost" && token) {
    config.params = new URLSearchParams(config.params);
    config.params.set(SITEMINDER_BYPASS_PARAM, token);
  }
  return config;
};
